import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { BOTTOM_INDENTS, COLORS, RIGHT_INDENTS, ROUTE_PATHS } from '@constants';
import { useStore } from '@hooks';
import { Button, Icon, Typography } from '@ui';
import { Divider, Flex } from 'antd';
import { observer } from 'mobx-react';
import dataURItoBlob from '../../../../utils/dataURItoBlob';
import getBulkUploadErrorMessage from '../../../../utils/getBulkUploadErrorMessage';
import { usePreWizardContext } from '../../PreWizardContext';
import { Container, StyledIcon, StyledStatusWrapper, StyledUpload } from './styled';

const Step0ManualCsv = observer(() => {
    const navigate = useNavigate();
    const store = useStore();
    const { isManualStoreWithNoCampaigns } = store.accountsStore;
    const { bulkUploadPending, bulkUpload } = store.productsStore;
    const { setIsButtonEnabled, setOnButtonClick } = usePreWizardContext();

    const [error, setError] = useState(false);
    const [validRecords, setValidRecords] = useState(0);

    const handleDownloadExample = () => {
        const link = document.createElement('a');
        link.href = '/bulk_examples/products.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const beforeUpload = (file) => {
        if (file.size > 10485760) {
            setError('There was an error uploading the file. Please ensure file is less than 10 MB.');
            return false;
        }
        return true;
    };

    const handleChange = ({ file }) => {
        if (file.status === 'uploading') {
            setValidRecords(0);
            setError(false);
            return;
        }
        if (file.status === 'done') {
            const reader = new FileReader();
            reader.onload = async () => {
                try {
                    const result = reader.result;
                    const formData = new FormData();
                    formData.append('file', dataURItoBlob(result));
                    const response = await bulkUpload(formData);
                    handleResponse(response);
                } catch (err) {
                    const message =
                        getBulkUploadErrorMessage(err) ||
                        'Upload failed due to a table structure error. See the CSV Structure Guide for the correct structure.';
                    setValidRecords(0);
                    setError(message);
                }
            };

            reader.readAsDataURL(file.originFileObj);
        }
    };

    const handleResponse = (response) => {
        if (response?.status === 'added-to-queue') {
            setValidRecords(response.validRecords);
            return;
        }

        // API may return 200 with error payload { status: 400, code, errors }
        const message =
            getBulkUploadErrorMessage(response) ||
            'Upload failed due to a table structure error. See the CSV Structure Guide for the correct structure.';
        setValidRecords(0);
        setError(message);
    };

    const handleCreateCampaignCsv = useCallback(() => {
        navigate(ROUTE_PATHS.CAMPAIGNS_CREATE_STEP_1);
    }, [navigate]);

    useEffect(() => {
        setIsButtonEnabled(validRecords > 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validRecords]);

    useEffect(() => {
        const handlerWrapper = () => handleCreateCampaignCsv;
        setOnButtonClick(handlerWrapper);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleCreateCampaignCsv]);

    useEffect(() => {
        if (validRecords > 0) {
            setOnButtonClick(() => handleCreateCampaignCsv);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validRecords, handleCreateCampaignCsv]);

    if (!isManualStoreWithNoCampaigns) {
        return <Navigate to={ROUTE_PATHS.CAMPAIGNS} replace />;
    }

    return (
        <Flex vertical style={{ flexGrow: 1 }}>
            <Container>
                <Typography.Title level={2} style={{ marginBottom: '24px' }}>
                    Upload via CSV
                </Typography.Title>

                <StyledStatusWrapper $error={error}>
                    <Flex align='center' justify='space-between' style={{ width: '100%' }} gap='24px'>
                        <Flex align='center' gap='24px'>
                            <Icon
                                color={COLORS.$gray40Black}
                                size='80px'
                                component={() => <Icon.EntireCatalogIcon />}
                            />
                            <Flex vertical>
                                <Typography.Text type='badgeLarge' style={BOTTOM_INDENTS.XXS}>
                                    {validRecords
                                        ? `${validRecords.toLocaleString('en-US')} products added to queue`
                                        : '0 products'}
                                </Typography.Text>
                                {error ? (
                                    <Typography.Text
                                        type='descriptor1'
                                        color={COLORS.$tartRed}
                                        style={{ fontWeight: 500, whiteSpace: 'pre-line' }}
                                    >
                                        {error}
                                    </Typography.Text>
                                ) : validRecords ? (
                                    <Typography.Text type='descriptor1'>
                                        <Icon
                                            size='24px'
                                            component={() => <Icon.CheckCircleIcon />}
                                            style={RIGHT_INDENTS.XXS}
                                        />
                                        Products will appear on the Products page when processing is complete.
                                    </Typography.Text>
                                ) : (
                                    <Typography.Text type='descriptor1'>
                                        Add products via CSV file (maximum file size — 10MB)
                                    </Typography.Text>
                                )}
                            </Flex>
                        </Flex>
                        <StyledUpload
                            accept='.csv'
                            customRequest={({ file, onSuccess }) => {
                                setTimeout(() => onSuccess('ok'), 0);
                            }}
                            $pending={bulkUploadPending}
                            beforeUpload={beforeUpload}
                            onChange={handleChange}
                            showUploadList={false}
                        >
                            <Button
                                type={validRecords ? 'default' : 'primary'}
                                size='large'
                                icon={
                                    <StyledIcon
                                        $pending={bulkUploadPending}
                                        color={validRecords ? COLORS.$gray60Black : COLORS.$white}
                                        size='24px'
                                        component={() => (bulkUploadPending ? <Icon.ResetIcon /> : <Icon.UploadIcon />)}
                                    />
                                }
                            >
                                {validRecords ? 'Update Products' : 'Upload Products'}
                            </Button>
                        </StyledUpload>
                    </Flex>
                </StyledStatusWrapper>

                <Flex align='flex-start' gap='48px' style={{ marginBottom: '24px', flexWrap: 'wrap' }}>
                    <Flex vertical style={{ flex: '1 0 0', minWidth: '0' }}>
                        <Typography.Text type='descriptor1' color={COLORS.$gray60Black}>
                            <strong>Processing time:</strong> Once uploaded, it may take some time for the system to
                            process your file. You&apos;ll see your updated catalog on the Products page when processing
                            is complete.
                        </Typography.Text>
                    </Flex>
                    <Flex vertical style={{ flex: '1 0 0', minWidth: '0' }}>
                        <Typography.Text type='descriptor1' color={COLORS.$gray60Black}>
                            <strong>Updating products:</strong> You can update existing products by uploading a new CSV
                            with matching productIDs. All corresponding product information will be updated when file
                            upload is complete.
                        </Typography.Text>
                    </Flex>
                </Flex>

                <Divider style={{ margin: '24px 0', borderTop: `1px solid ${COLORS.$gray15Black}` }} />

                <Flex justify='space-between' align='center'>
                    <Typography.Title level={2}>Need help formatting your CSV?</Typography.Title>
                    <Flex align='center' gap='16px'>
                        <Button
                            type='default'
                            size='large'
                            iconLeft={
                                <Icon color={COLORS.$gray60Black} size='24px' component={() => <Icon.InfoIcon />} />
                            }
                            onClick={() => window.open('/docs/api#tag/product_schema', '_blank')}
                        >
                            View the CSV Structure Guide
                        </Button>
                        <Button
                            type='default'
                            size='large'
                            iconLeft={
                                <Icon color={COLORS.$gray60Black} size='24px' component={() => <Icon.DownloadIcon />} />
                            }
                            onClick={handleDownloadExample}
                        >
                            Download template
                        </Button>
                    </Flex>
                </Flex>
            </Container>
        </Flex>
    );
});

export default Step0ManualCsv;
